<template>
  <!--
    Nothing renders unless the backend handed us a snapshot: `aiUsage` is null
    for non-admins and on any server that hasn't shipped the field yet, and an
    empty/errored card would be worse than no card at all.
  -->
  <div v-if="hasUsage" data-testid="ai-usage-panel"
    class="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
    <div class="flex items-start justify-between gap-2">
      <div>
        <h3 class="text-sm font-medium text-gray-800">AI usage</h3>
        <p class="text-xs text-gray-500" data-testid="ai-usage-period">{{ periodLabel }}</p>
      </div>
      <span v-if="chipLabel" data-testid="ai-usage-chip" class="text-[11px] font-medium px-2 py-0.5 rounded-full"
        :class="chipClass">{{ chipLabel }}</span>
    </div>

    <!-- Capped: spend against the ceiling, with a progress bar and what's left. -->
    <div v-if="!isUncapped" class="mt-3">
      <div class="flex items-baseline justify-between gap-2">
        <p class="text-sm text-gray-700" data-testid="ai-usage-monthly-spend">
          <span class="text-lg font-bold text-gray-900">{{ monthlySpendLabel }}</span>
          <span class="text-xs text-gray-500"> of {{ monthlyCapLabel }} this period</span>
        </p>
        <span class="text-xs font-medium text-gray-500" data-testid="ai-usage-percent">{{ monthlyPercent }}%</span>
      </div>

      <div class="w-full bg-gray-100 rounded-full h-1.5 mt-2">
        <div class="h-1.5 rounded-full transition-all" :class="barClass" :style="{ width: `${monthlyPercent}%` }"
          data-testid="ai-usage-bar"></div>
      </div>

      <p class="text-xs text-gray-600 mt-2" data-testid="ai-usage-remaining">
        <span class="font-semibold text-gray-800">{{ monthlyRemainingLabel }}</span> remaining this period
      </p>
    </div>

    <!--
      Uncapped: no ceiling exists, so there is nothing to draw a progress bar
      against. Show the running total and say plainly that there is no limit.
    -->
    <div v-else class="mt-3" data-testid="ai-usage-uncapped">
      <p class="text-lg font-bold text-gray-900" data-testid="ai-usage-monthly-spend">{{ monthlySpendLabel }}</p>
      <p class="text-xs text-gray-500">spent this period — your school has no monthly spending limit.</p>
    </div>

    <p class="text-xs text-gray-500 mt-2" data-testid="ai-usage-tokens">
      {{ monthlyTokensLabel }} tokens used this period
    </p>

    <!-- Daily figures only exist when a daily cap is set; otherwise omit the row. -->
    <div v-if="hasDailyCap" class="mt-3 pt-3 border-t border-gray-100" data-testid="ai-usage-daily">
      <div class="flex items-baseline justify-between gap-2">
        <p class="text-xs text-gray-700" data-testid="ai-usage-daily-spend">
          <span class="font-semibold text-gray-900">{{ dailySpendLabel }}</span>
          <span class="text-gray-500"> of {{ dailyCapLabel }} today</span>
        </p>
        <span class="text-xs text-gray-500" data-testid="ai-usage-daily-remaining">
          {{ dailyRemainingLabel }} left
        </span>
      </div>
      <div class="w-full bg-gray-100 rounded-full h-1 mt-1.5">
        <div class="h-1 rounded-full bg-gray-400" :style="{ width: `${dailyPercent}%` }"
          data-testid="ai-usage-daily-bar"></div>
      </div>
      <p class="text-[11px] text-gray-500 mt-1" data-testid="ai-usage-daily-tokens">
        {{ dailyTokensLabel }} tokens today
      </p>
    </div>

    <!--
      Cap reached. Calm and factual: the school cannot raise its own limit, so
      we explain who can rather than offering a control that would fail.
    -->
    <div v-if="capReached" class="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-2.5"
      data-testid="ai-usage-cap-reached">
      <div class="flex items-start gap-1.5">
        <i class="pi pi-pause-circle text-gray-500 text-sm mt-0.5"></i>
        <p class="text-xs text-gray-700">
          The assistant is paused for this period because the spending limit has been reached. It resumes
          automatically when the next period starts. Limits are set by the platform team — contact support if
          your school needs a higher one.
        </p>
      </div>
    </div>

    <!-- Approaching the cap: visible, but not alarming. -->
    <div v-else-if="isApproachingCap" class="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5"
      data-testid="ai-usage-warning">
      <div class="flex items-start gap-1.5">
        <i class="pi pi-info-circle text-amber-600 text-sm mt-0.5"></i>
        <p class="text-xs text-amber-800">
          You have used {{ monthlyPercent }}% of this period's limit. The assistant pauses on its own once the
          limit is reached, so you may want to pace usage for the rest of the period.
        </p>
      </div>
    </div>

    <p v-if="hasUnpricedUsage" class="text-[11px] text-gray-500 mt-2" data-testid="ai-usage-unpriced">
      Some recent usage has not been priced yet, so the amount shown may be lower than the real total.
    </p>
  </div>
</template>

<script setup lang="ts">
import { useSchoolAiUsage } from '@/composables/useSchoolAiUsage';

/**
 * Detailed, plan-your-usage view of `School.aiUsage`, shown beneath the AI
 * assistant toggles in Settings — the place an admin already goes to manage the
 * assistant. READ-ONLY: schools cannot edit their own cap.
 */
const {
  hasUsage,
  isUncapped,
  hasDailyCap,
  capReached,
  isApproachingCap,
  hasUnpricedUsage,
  monthlyPercent,
  dailyPercent,
  monthlySpendLabel,
  monthlyCapLabel,
  monthlyRemainingLabel,
  monthlyTokensLabel,
  dailySpendLabel,
  dailyCapLabel,
  dailyRemainingLabel,
  dailyTokensLabel,
  periodLabel,
  barClass,
  status,
} = useSchoolAiUsage();

const CHIP_LABELS: Record<string, string> = {
  reached: 'Paused — limit reached',
  warning: 'Approaching limit',
  uncapped: 'No limit',
};

const CHIP_CLASSES: Record<string, string> = {
  reached: 'bg-gray-100 text-gray-700',
  warning: 'bg-amber-100 text-amber-800',
};

const chipLabel = computed(() => CHIP_LABELS[status.value] ?? '');
const chipClass = computed(() => CHIP_CLASSES[status.value] ?? 'bg-gray-100 text-gray-600');
</script>
